/* eslint-disable import/no-default-export */
interface CustomMeasurement {
  name: string;
  totalTime: number;
}

export const PerfKeys: { [key: string]: string } = {
  NAVIGATION: 'startup:navigation',
  NAVIGATION_TLS: 'startup:navigation-tls',
  NAVIGATION_TTFB: 'startup:navigation-ttfb',
  HTML: 'startup:html',
  TIME_TO_INTERACTIVE: 'startup:time-to-interactive',
  LOAD_TO_INTERACTIVE: 'startup:page-load-to-interactive',
  APP: 'startup:app.js',
  APP_DOWNLOAD: 'download:app.js',
  LTP: 'startup:ltp.js',
  LTP_DOWNLOAD: 'download:ltp.js',
  FIRST_RENDER: 'startup:first-render',
  QL_MEMBER_HEADER: 'quickload:memberHeader',
  QL_MEMBER_BOARDS: 'quickload:memberBoards',
  QL_MEMBER_QUICK_BOARDS: 'quickload:memberQuickBoards',
  QL_SEARCH: 'quickload:quickBoardSearch',
  QL_BOARD_MIN: 'quickload:currentBoardMinimal',
  QL_BOARD_SECONDARY: 'quickload:currentBoardSecondary',
  QL_BOARD_PLUGINS: 'quickload:currentBoardPluginData',
  QL_CARD: 'quickload:card',
  QL_ORGS: 'quickload:organizationBoardsPage',
  BOOTSTRAP_HOME_VIEW: 'bootstrap:member-home-view',
  BOOTSTRAP_SUPERHOME_VIEW: 'bootstrap:member-super-home-view',
  BOOTSTRAP_CARD_VIEW: 'bootstrap:card-view',
  BOOTSTRAP_BOARD_VIEW: 'bootstrap:boards-view',
  BOOTSTRAP_IN_APP_GALLERY_VIEW: 'bootstrap:in-app-gallery-view',
};

const supported = 'performance' in window;
const noop = (key: string): void => {};
const keySet: string[] = Object.keys(PerfKeys).map((key) => PerfKeys[key]);

class Perf {
  private measurementCache: CustomMeasurement[] = [];
  private startupReported = false;
  Keys = PerfKeys;

  private cacheMetrics(metrics: CustomMeasurement[]) {
    this.measurementCache = this.measurementCache.concat(metrics);
  }

  startMeasurement = !supported
    ? noop
    : (key: string): void => {
        if (keySet.indexOf(key) === -1) {
          return;
        }
        try {
          window.performance.mark(`${key}-start`);
        } catch (e) {
          console.error(e);
        }
      };

  stopMeasurement = !supported
    ? noop
    : (key: string): void => {
        if (keySet.indexOf(key) === -1) {
          return;
        }
        try {
          window.performance.mark(`${key}-end`);
          window.performance.measure(key, `${key}-start`, `${key}-end`);
          const measurement: PerformanceEntry = window.performance.getEntriesByName(
            key,
          )[0];
          this.cacheMetrics([{ name: key, totalTime: measurement.duration }]);
        } catch (e) {
          console.error(e);
        }
      };

  reportStartupMetrics() {
    if (this.startupReported) {
      return;
    }

    try {
      performance.mark('done');
      window.performance.measure(
        PerfKeys.LOAD_TO_INTERACTIVE,
        'startup:html-start',
        'done',
      );

      const nav = window.performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceResourceTiming;
      const ltp = (window.performance.getEntriesByType(
        'resource',
      ) as PerformanceResourceTiming[]).filter(
        (r) => r.initiatorType === 'script' && r.name.includes('ltp'),
      )[0];
      const app = (window.performance.getEntriesByType(
        'resource',
      ) as PerformanceResourceTiming[]).filter(
        (r) => r.initiatorType === 'script' && r.name.includes('app'),
      )[0];
      const lti: PerformanceEntry = window.performance.getEntriesByName(
        PerfKeys.LOAD_TO_INTERACTIVE,
      )[0];
      const done: PerformanceMark = window.performance.getEntriesByName(
        'done',
      )[0];

      this.cacheMetrics([
        {
          name: PerfKeys.NAVIGATION,
          totalTime: nav.responseEnd - nav.fetchStart,
        },
        {
          name: PerfKeys.NAVIGATION_TLS,
          totalTime:
            nav.secureConnectionStart > 0
              ? nav.connectEnd - nav.secureConnectionStart
              : 0,
        },
        {
          name: PerfKeys.NAVIGATION_TTFB,
          totalTime: nav.responseStart - nav.requestStart,
        },
        {
          name: PerfKeys.TIME_TO_INTERACTIVE,
          totalTime: lti.duration,
        },
        {
          name: PerfKeys.LOAD_TO_INTERACTIVE,
          totalTime: done.startTime - nav.startTime,
        },
        {
          name: PerfKeys.LTP_DOWNLOAD,
          totalTime: ltp.responseEnd - ltp.responseStart,
        },
        {
          name: PerfKeys.APP_DOWNLOAD,
          totalTime: app.responseEnd - app.responseStart,
        },
      ]);
    } catch (e) {
      console.error(e);
    }
    this.startupReported = true;
  }

  getCachedMetrics(): CustomMeasurement[] {
    return this.measurementCache;
  }

  clearCachedMetrics() {
    this.measurementCache = [];
  }
}

// Assign this directly on the window to use directly in the html ejs template
window['TrelloPerf'] = window['TrelloPerf'] ? window['TrelloPerf'] : new Perf();
export default window['TrelloPerf'];
